'use client';
import { AgreementSampleScenarioSelect } from './AgreementSampleScenarioSelect';
import {
  AgreementDocumentPayload,
  AgreementSampleScenario,
  AgreementTemplateContent,
  AgreementTemplateMissing,
  buildAgreementFixtureResolution,
  compileAgreementTemplate,
} from 'bff';
import { DocumentPreviewFrame, renderDocument } from 'documents/client';
import { useTranslations } from 'i11n';
import { useMemo, useState } from 'react';
import { Badge } from 'ui';

const PREVIEW_GENERATED_AT = new Date().toISOString();

/**
 * The same React component the PDF prints, over the guided fixture the author
 * picks (installments and fees, each on or off) and resolved exactly like the
 * wizard resolves a draft. No Lambda: everything is pure bff code.
 */
export const AgreementTemplatePreview = ({
  content,
  companyName,
  templateName,
  version,
}: {
  content: AgreementTemplateContent;
  companyName: string;
  templateName: string;
  version: number;
}) => {
  const t = useTranslations();
  const [scenario, setScenario] = useState<AgreementSampleScenario>(
    'guidedSinglePayment',
  );

  const resolution = useMemo(
    () => buildAgreementFixtureResolution(scenario),
    [scenario],
  );

  const compiled = useMemo(
    () =>
      compileAgreementTemplate({
        content,
        variables: resolution.variables,
        ruleContext: resolution.context,
        stage: 'send',
        placeholders: 'preview',
      }),
    [content, resolution],
  );

  const payload = useMemo<AgreementDocumentPayload>(
    () => ({
      meta: {
        branding: {
          company_name: companyName,
          tax_id: null,
          logo_data_uri: null,
        },
        title: t('document.agreement.name'),
        reference: 'RIV-1913',
        generated_at: PREVIEW_GENERATED_AT,
        country_code: 'ARG',
        variant: content.layout_variant,
      },
      document: {
        id: 'preview',
        kind: 'preview',
        template_name: templateName,
        version,
        claim_ref_id: 'RIV-1913',
      },
      blocks: compiled.blocks,
      signers: content.signers,
      watermark: null,
    }),
    [companyName, compiled.blocks, content, t, templateName, version],
  );

  // Una clave que no corresponde en este escenario no es un hueco: no imprime
  // nada y listarla es el ruido que el selector viene a resolver.
  const gaps = useMemo(
    () =>
      compiled.missing
        .filter((gap) => gap.class !== 'not_applicable')
        .sort((left, right) => Number(right.blocking) - Number(left.blocking)),
    [compiled.missing],
  );

  return (
    <div className='space-y-4'>
      {/* <p className='text-xs text-muted-foreground'>
        {t('agreementTemplates.editor.previewNote')}
      </p> */}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <AgreementSampleScenarioSelect
          value={scenario}
          onChange={setScenario}
        />
        {gaps.length > 0 && (
          <div className='space-y-1'>
            <p className='text-xs font-medium'>
              {t('agreementTemplates.editor.previewMissing')}
            </p>
            <div className='flex flex-col gap-1'>
              {gaps.map((gap) => (
                <MissingBadge key={gap.key} gap={gap} />
              ))}
            </div>
          </div>
        )}
      </div>

      {compiled.syntax_errors.length > 0 && (
        <ul className='space-y-1 text-xs text-destructive'>
          {compiled.syntax_errors.map((error) => (
            <li key={error.block_id}>
              {error.block_id}: {error.message}
            </li>
          ))}
        </ul>
      )}

      <div className='overflow-auto rounded-xl border bg-zinc-100 p-4'>
        <div className='mx-auto w-fit bg-white p-6 shadow-sm'>
          <DocumentPreviewFrame template='agreement'>
            {renderDocument('agreement', payload)}
          </DocumentPreviewFrame>
        </div>
      </div>
    </div>
  );
};

export const MissingBadge = ({ gap }: { gap: AgreementTemplateMissing }) => {
  const t = useTranslations();

  return (
    <Badge
      className='w-fit'
      variant={gap.blocking ? 'destructive' : 'secondary'}
    >
      <span className='font-mono'>{gap.key}</span>
      {' · '}
      {gap.reason
        ? t(`offer.agreement.reasons.${gap.reason}`)
        : t(`agreementTemplates.missingStatus.${gap.status}`)}
    </Badge>
  );
};
